import { useState } from "react";
import { Users, Briefcase } from "lucide-react";
import ResidentSheet from "./ResidentSheet";
import VolunteerBadge from "../VolunteerBadge";
import { useT } from "../../lib/i18n";

const ACTIVITY_EMOJIS = {
  hiking: "🥾", yoga: "🧘", cooking: "🍳", music: "🎵",
  art: "🎨", fitness: "💪", entrepreneurship: "🚀",
};

const isNewResident = (profile) => {
  if (!profile?.arrival_date) return false;
  try {
    const arrived = new Date(profile.arrival_date);
    const now = new Date();
    const days = Math.floor((now - arrived) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= 7;
  } catch {
    return false;
  }
};

const MembersTab = ({ communityProfiles, communityPets, introductions, isRetreat = false }) => {
  const [selectedResident, setSelectedResident] = useState(null);
  const { t } = useT();

  // "Meet Your Team": pinned staff first (Founder / Community Manager etc.),
  // then any current resident flagged as a Team Member. The backend supplies
  // a `team_role` label for each — we just filter to the people who have one.
  const teamMembers = (() => {
    const founders = communityProfiles.filter((p) => p.pinned && p.team_role);
    const volunteers = communityProfiles.filter((p) => !p.pinned && p.team_role);
    return [...founders, ...volunteers];
  })();
  const isFounder = (p) => !!p.pinned;
  const teamRoleLabel = (p) => p.team_role || (p.pinned ? "Founder" : "Team Member");
  // Retreat guests: the team lives in "Das Savi-Team" only — the group list is just the retreat participants.
  const listedProfiles = isRetreat ? communityProfiles.filter((p) => !p.team_role) : communityProfiles;
  return (
    <div className="space-y-6">
      {/* Header with Counts */}
      <div className="bg-gradient-to-br from-[#8C9A7C] to-[#7C8A6C] rounded-xl p-5 text-white">
        <h3 className="font-serif text-xl mb-3">{isRetreat ? t("Everyone on the retreat") : t("Who's in the house")}</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{listedProfiles.length}</p>
            <p className="text-xs text-white/80">{t("Humans")}</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{communityPets.counts?.dogs || 0}</p>
            <p className="text-xs text-white/80">{t("Dogs")}</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{communityPets.counts?.cats || 0}</p>
            <p className="text-xs text-white/80">{t("Cats")}</p>
          </div>
        </div>
      </div>

      {/* Meet Your Team — founders + current Team Members. Always above the rest. */}
      {teamMembers.length > 0 && (
        <div
          className="bg-gradient-to-br from-[#F3F5EF] to-[#E8EDE0] rounded-xl p-5 border border-[#8C9A7C]/20"
          data-testid="meet-your-team-section"
        >
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-serif text-lg text-[#1A1A1A]">{t("Meet your team")}</h3>
            <span className="text-[10px] uppercase tracking-wider text-[#5C6A4C] font-medium">
              {teamMembers.length} {t(teamMembers.length === 1 ? "person" : "people")}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {teamMembers.map((p) => {
              const founder = isFounder(p);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedResident(p)}
                  className="bg-white rounded-2xl p-3 text-left border border-stone-100 hover:border-[#5C6A4C]/40 transition-colors"
                  data-testid={`team-card-${p.id}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#8C9A7C] to-[#5C6A4C] flex items-center justify-center text-white font-serif text-base">
                      {p.avatar ? (
                        <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        p.name?.charAt(0)?.toUpperCase() || "?"
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2E2A26] text-sm leading-tight truncate">{p.name}</p>
                      <p
                        className={`text-[10px] font-medium uppercase tracking-wider mt-0.5 ${
                          founder ? "text-[#C85A38]" : "text-[#5C6A4C]"
                        }`}
                      >
                        {teamRoleLabel(p)}
                      </p>
                      {(p.occupation || p.fun_fact) && (
                        <p className="text-[11px] text-[#2E2A26]/65 leading-snug mt-1 line-clamp-2">
                          {p.occupation || p.fun_fact}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Arriving Soon */}
      {introductions.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-5 border border-amber-100 mt-6">
          <h3 className="font-serif text-lg text-[#1A1A1A] mb-3">Arriving soon</h3>
          <div className="space-y-3">
            {introductions.map((intro) => (
              <div key={intro.id} className="flex items-start gap-3 bg-white rounded-2xl p-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C85A38] to-[#F2CC8F] flex items-center justify-center text-white font-serif text-lg overflow-hidden flex-shrink-0">
                  {intro.avatar ? (
                    <img src={intro.avatar} alt={intro.name} className="w-full h-full object-cover" />
                  ) : (
                    intro.name?.charAt(0)
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-[#1A1A1A]">{intro.name}</p>
                    <VolunteerBadge show={intro.is_volunteer} />
                    {intro.nationality && <span className="text-xs text-[#1A1A1A]/60">{intro.nationality}</span>}
                  </div>
                  {intro.arriving_label && (
                    <p
                      className={`text-[11px] font-medium mt-0.5 ${
                        intro.status === "upcoming" ? "text-[#C85A38]" : "text-[#8C9A7C]"
                      }`}
                      data-testid={`intro-arrival-${intro.id}`}
                    >
                      {intro.arriving_label}
                    </p>
                  )}
                  {intro.occupation && (
                    <p className="text-xs text-[#8C9A7C] mt-0.5">{intro.occupation}</p>
                  )}
                  {intro.introduction && (
                    <p className="text-sm text-[#1A1A1A]/70 mt-2">{intro.introduction}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Members */}
      <div className="bg-white rounded-xl p-5 border border-stone-100" data-testid="residents-section">
        <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">{isRetreat ? t("Retreat group") : t("Current Residents")}</h3>
        {listedProfiles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {listedProfiles.map((profile) => {
              const isNew = isNewResident(profile);
              const interestKeys = Object.keys(ACTIVITY_EMOJIS).filter(
                (k) => profile.activities && profile.activities[k]
              ).slice(0, 4);
              return (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => setSelectedResident(profile)}
                  data-testid={`resident-card-${profile.id}`}
                  className="relative bg-[#F0EDE3] rounded-2xl p-4 text-left hover:bg-[#EFE9D0] hover:-translate-y-0.5 transition-all border border-transparent hover:border-[#C85A38]/30 focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                >
                  {isNew && (
                    <span className="absolute top-2 right-2 text-[10px] font-semibold bg-[#C85A38] text-white px-2 py-0.5 rounded-full shadow-sm">
                      New ✨
                    </span>
                  )}
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C85A38] to-[#F2CC8F] flex items-center justify-center text-white font-serif text-xl overflow-hidden flex-shrink-0 ring-2 ring-white shadow-sm">
                      {profile.avatar ? (
                        <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                      ) : (
                        profile.name?.charAt(0)
                      )}
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-medium text-[#1A1A1A] leading-tight truncate">{profile.name}</p>
                        <VolunteerBadge show={profile.is_volunteer} />
                      </div>
                      <p className="text-[11px] text-[#C85A38] mt-0.5 truncate">
                        {profile.nationality || t("Citizen of the world")}
                      </p>
                      {profile.occupation && (
                        <p className="text-xs text-[#1A1A1A]/70 mt-1 truncate">{profile.occupation}</p>
                      )}
                      {profile.current_project && (
                        <p className="text-[11px] text-[#1A1A1A]/50 mt-0.5 line-clamp-1 italic">
                          {profile.current_project}
                        </p>
                      )}
                    </div>
                  </div>
                  {(interestKeys.length > 0 || (profile.bringing_pet && profile.pet_name)) && (
                    <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                      {interestKeys.map((k) => (
                        <span
                          key={k}
                          title={k}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/70 text-sm leading-none"
                        >
                          {ACTIVITY_EMOJIS[k]}
                        </span>
                      ))}
                      {profile.bringing_pet && profile.pet_name && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#8C9A7C] bg-white/70 rounded-full px-2 py-0.5">
                          {profile.pet_type === "cat" ? "🐱" : "🐕"} {profile.pet_name}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-[#1A1A1A]/20 mx-auto mb-3" />
            <p className="text-[#1A1A1A]/60">
              {t("Community profiles will appear here once more members complete onboarding.")}
            </p>
          </div>
        )}
      </div>

      <ResidentSheet
        resident={selectedResident}
        open={!!selectedResident}
        onOpenChange={(open) => { if (!open) setSelectedResident(null); }}
      />

      {/* All Pets Section */}
      <div className="bg-white rounded-xl p-5 border border-stone-100">
        <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">{t("Furry friends at Savi")}</h3>
        
        {/* Resident Dogs */}
        <div className="mb-4">
          <p className="text-sm font-medium text-[#1A1A1A]/60 mb-3">{t("Savi resident dogs")}</p>
          <div className="grid grid-cols-3 gap-3">
            {communityPets.pets?.filter(p => p.is_resident).map((pet) => (
              <div key={pet.id} className="text-center">
                <div className="w-20 h-20 mx-auto rounded-full overflow-hidden mb-2 border-4 border-[#F0EDE3]">
                  {pet.photo ? (
                    <img src={pet.photo} alt={pet.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#F0EDE3] flex items-center justify-center text-3xl">
                      {pet.type === "cat" ? "🐱" : "🐕"}
                    </div>
                  )}
                </div>
                <p className="font-medium text-[#1A1A1A] text-sm">{pet.name}</p>
                {pet.bio && <p className="text-[10px] text-[#C85A38]">{pet.bio}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* Guest Pets */}
        {communityPets.pets?.filter(p => !p.is_resident).length > 0 && (
          <div className="border-t border-stone-100 pt-4">
            <p className="text-sm font-medium text-[#1A1A1A]/60 mb-3">{t("Guest pets")}</p>
            <div className="grid grid-cols-3 gap-3">
              {communityPets.pets?.filter(p => !p.is_resident).map((pet) => (
                <div key={pet.id} className="text-center">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden mb-2 border-4 border-[#8C9A7C]/30">
                    {pet.photo ? (
                      <img src={pet.photo} alt={pet.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#F0EDE3] flex items-center justify-center text-3xl">
                        {pet.type === "cat" ? "🐱" : "🐕"}
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-[#1A1A1A] text-sm">{pet.name}</p>
                  {pet.breed && <p className="text-[10px] text-[#1A1A1A]/60">{pet.breed}</p>}
                  <p className="text-[10px] text-[#8C9A7C]">{t("with")} {pet.owner}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembersTab;
